import { useState, useRef, type DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PLANBASE_CONFIG } from '../config';
import { getUserAuthHeaders } from '../utils/auth';
import {
  ArrowPathIcon,
  ArrowUpTrayIcon,
  DocumentArrowUpIcon,
  PencilSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../lib/utils';

/**
 * Chooser page shown before /cms-upload. Users pick either the manual
 * form path (unchanged) or import-from-PDF (task R). Import path posts
 * the file to /sdk/planbase/projects/import-pdf, hydrates the extracted
 * fields into sessionStorage under `planbase_pdf_import`, then routes
 * to the upload form which reads them on mount.
 *
 * The PDF path fails gracefully — a scanned-only PDF or a parse error
 * still routes the user to the manual form with a toast, so the
 * import never becomes a dead end.
 */

const PDF_MAX_MB = 40;
const PDF_STORAGE_KEY = 'planbase_pdf_import';

export default function UploadChooser() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'chooser' | 'pdf'>('chooser');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const goManual = () => navigate('/cms-upload');

  const handleFile = async (file: File) => {
    if (busy) return;
    if (file.size > PDF_MAX_MB * 1024 * 1024) {
      toast.error(`That PDF is over ${PDF_MAX_MB} MB — try trimming it first.`);
      return;
    }
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('That file is not a PDF.');
      return;
    }

    setBusy(true);
    setBusyLabel('Reading pages…');
    const toastId = toast.loading('Extracting from PDF…');
    try {
      const form = new FormData();
      form.append('file', file);

      setBusyLabel('Structuring with Orbi…');
      const headers = { ...getUserAuthHeaders() } as Record<string, string>;
      // Multipart — drop the JSON Content-Type the shared helper adds
      // so the browser sets the boundary itself.
      delete headers['Content-Type'];

      const res = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/projects/import-pdf`, {
        method: 'POST',
        headers,
        body: form,
      });
      // Read as text first so a non-JSON body (bare platform 500, plain
      // text error, empty response) still yields something we can show
      // the user instead of a silent JSON parse error.
      const rawBody = await res.text();
      let data: {
        fields?: Record<string, unknown>;
        images?: string[];
        coverSuggestion?: string;
        pageCount?: number;
        error?: string;
        detail?: string;
        hint?: string;
      };
      try {
        data = JSON.parse(rawBody);
      } catch {
        data = { error: `Server returned non-JSON (${res.status}): ${rawBody.slice(0, 200)}` };
      }
      // Log the full response for diagnostics; user can screenshot the
      // console when the toast alone isn't enough to explain a failure.
      console.log('[pdf-import] status', res.status, 'response:', data);
      if (!res.ok) {
        const detail = data.detail ? ` — ${data.detail}` : '';
        throw new Error(`${data.error || 'Import failed'}${detail} (HTTP ${res.status})`);
      }

      const hasAnyField =
        data.fields && Object.values(data.fields).some((v) => v && String(v).length);
      const imageCount = Array.isArray(data.images) ? data.images.length : 0;
      if (!hasAnyField && imageCount === 0) {
        toast.info(
          data.hint || "Couldn't pull structured fields — you can fill the form manually.",
          { id: toastId },
        );
      } else {
        const imgLabel = imageCount > 0 ? ` and ${imageCount} image${imageCount === 1 ? '' : 's'}` : '';
        toast.success(
          `Read ${data.pageCount || '—'} pages${imgLabel}. Review the pre-filled form.`,
          { id: toastId },
        );
      }

      // Hand off to UploadProject via sessionStorage so the URL stays clean.
      sessionStorage.setItem(
        PDF_STORAGE_KEY,
        JSON.stringify({
          source: 'pdf',
          fileName: file.name,
          pageCount: data.pageCount,
          fields: data.fields || {},
          images: Array.isArray(data.images) ? data.images : [],
          coverSuggestion: String(data.coverSuggestion || ''),
          importedAt: new Date().toISOString(),
        }),
      );
      navigate('/cms-upload?source=pdf');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      toast.error(message, { id: toastId });
      // Take them to the manual form anyway so the flow never dead-ends.
      navigate('/cms-upload');
    } finally {
      setBusy(false);
      setBusyLabel('');
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 md:py-14">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Add a project
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Fill it out from scratch, or drop a PDF and Orbi will pre-fill what it can.
        </p>
      </div>

      {mode === 'chooser' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Manual */}
          <button
            type="button"
            onClick={goManual}
            className="group flex h-full flex-col items-start gap-4 rounded-2xl border border-border bg-card p-6 text-left transition-colors hover:border-primary/60"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-foreground">
              <PencilSquareIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-semibold text-foreground">
                Fill out manually
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Type the title, description, and metadata, then add your images. The classic flow.
              </p>
            </div>
            <span className="mt-auto text-xs font-medium uppercase tracking-wide text-primary">
              Start with a blank form →
            </span>
          </button>

          {/* Import from PDF */}
          <button
            type="button"
            onClick={() => setMode('pdf')}
            className="group flex h-full flex-col items-start gap-4 rounded-2xl border border-border bg-card p-6 text-left transition-colors hover:border-primary/60"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <DocumentArrowUpIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-foreground">Import from PDF</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                  New
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Drop an issued portfolio PDF. Orbi extracts the title, description, category, and tags.
              </p>
            </div>
            <span className="mt-auto text-xs font-medium uppercase tracking-wide text-primary">
              Choose a PDF →
            </span>
          </button>
        </div>
      ) : (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={cn(
              'flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-12 text-center transition-colors',
              dragOver ? 'border-primary bg-primary/5' : 'border-border bg-card',
              busy && 'pointer-events-none opacity-70',
            )}
          >
            {busy ? (
              <>
                <ArrowPathIcon className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium text-foreground">{busyLabel}</p>
                <p className="text-xs text-muted-foreground">
                  This can take 10–20 seconds for a long portfolio.
                </p>
              </>
            ) : (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <ArrowUpTrayIcon className="h-6 w-6" />
                </div>
                <p className="text-base font-medium text-foreground">
                  Drop a PDF here, or{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    choose one
                  </button>
                </p>
                <p className="text-xs text-muted-foreground">
                  Up to {PDF_MAX_MB} MB. Text-based PDFs work best; scanned ones may not extract fields.
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
              aria-label="Choose a PDF to import"
            />
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
            <button
              type="button"
              onClick={() => setMode('chooser')}
              disabled={busy}
              className="hover:text-foreground disabled:opacity-50"
            >
              ← Back
            </button>
            <span className="inline-flex items-center gap-1.5">
              <SparklesIcon className="h-3.5 w-3.5" />
              Orbi never keeps your PDF — only the extracted fields.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
